import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import {
  Button,
  InlineNotification,
  Search,
  SkeletonPlaceholder,
  Tag,
  Loading,
  TextInput,
  TextArea,
  ToastNotification,
  Select,
  SelectItem,
  Checkbox,
  Toggle,
  Modal
} from '@carbon/react';
import { Add, Close, Group, CheckmarkFilled, ArrowRight, ArrowLeft, User, UserFollow, ChevronLeft, ChevronRight, Activity, Checkmark, TrashCan, CheckboxChecked } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import PageContainer from '../components/PageContainer';
import { usePermissions } from '../contexts/PermissionContext';
import './Groups.css';

const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0) : '';
  const last = lastName ? lastName.charAt(0) : '';
  return (first + last).toUpperCase() || '?';
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('sr-Latn-RS', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Activity type color mapping (consistent with ResidentProfile)
const getActivityTypeColor = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'meal': case 'ishrana': case 'nutrition': return '#0f62fe';
    case 'therapy': case 'terapija': case 'medical': return '#da1e28';
    case 'exercise': case 'vezbe': case 'sport': case 'physical': return '#9fb746';
    case 'social': case 'socijalne': case 'entertainment': return '#8a3ffc';
    case 'visit': case 'poseta': return '#0f62fe';
    default: return '#6f6f6f';
  }
};

// Translate activity type values to Serbian for display
const translateActivityType = (type) => {
  switch ((type || '').toLowerCase()) {
    case 'nutrition': case 'meal': case 'ishrana': return 'Ishrana';
    case 'physical': case 'exercise': case 'vezbe': case 'sport': return 'Fizička aktivnost';
    case 'medical': case 'medicinska': return 'Medicinska aktivnost';
    case 'nursing': case 'nega': return 'Nega';
    case 'visit': case 'poseta': return 'Poseta';
    case 'entertainment': case 'zabava': return 'Zabava';
    default: return type;
  }
};

// ===========================================
// Group Add Activity Sidebar Component
// (Identical design to Resident AddActivitySidebar)
// ===========================================
const GroupAddActivitySidebar = ({ isOpen, onClose, group, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [stepTransition, setStepTransition] = useState('entering');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const stepTransitionTimeout = useRef(null);

  // Step 1 - Activity selection (search + paging)
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(6);
  const [pageFading, setPageFading] = useState(false);

  // Step 2 - Summary / details
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [notes, setNotes] = useState('');
  const [repeatFrequency, setRepeatFrequency] = useState('none');
  const [repeatDays, setRepeatDays] = useState([]);
  const [taskCreation, setTaskCreation] = useState(false);
  const [severity, setSeverity] = useState(3);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const formatDateForInput = (d) => {
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  // Weekday helpers (payload uses lowercase English day names)
  const WEEKDAYS = [
    { value: 'monday', label: 'Ponedeljak' },
    { value: 'tuesday', label: 'Utorak' },
    { value: 'wednesday', label: 'Sreda' },
    { value: 'thursday', label: 'Četvrtak' },
    { value: 'friday', label: 'Petak' },
    { value: 'saturday', label: 'Subota' },
    { value: 'sunday', label: 'Nedelja' },
  ];

  // Returns the lowercase English weekday name for a yyyy-MM-dd date string
  const getWeekdayFromDate = (dateStr) => {
    if (!dateStr) return null;
    const [yr, mo, dy] = dateStr.split('-').map(Number);
    const dt = new Date(yr, mo - 1, dy);
    return WEEKDAYS[dt.getDay() === 0 ? 6 : dt.getDay() - 1].value;
  };

  const handleDateChange = (value) => {
    setDate(value);
    const weekday = getWeekdayFromDate(value);
    if (weekday) {
      // Auto-check the weekday matching the selected date; it cannot be unchecked
      setRepeatDays(prev => (prev.includes(weekday) ? prev : [...prev, weekday]));
    }
  };

  const toggleRepeatDay = (dayValue) => {
    const weekday = getWeekdayFromDate(date);
    // The day matching the selected date is locked and cannot be unchecked
    if (dayValue === weekday) return;
    setRepeatDays(prev =>
      prev.includes(dayValue) ? prev.filter(d => d !== dayValue) : [...prev, dayValue]
    );
  };

  const fetchActivities = async () => {
    setActivitiesLoading(true);
    try {
      const res = await axiosInstance.get('/api/Activity');
      setActivities(res.data?.data || []);
    } catch {
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setStepTransition('entering');
      setSelectedActivity(null);
      setSearchTerm('');
      setPage(1);
      setDate(formatDateForInput(new Date()));
      setStartTime('09:00');
      setEndTime('10:00');
      setNotes('');
      setRepeatFrequency('none');
      setRepeatDays([]);
      setTaskCreation(false);
      setSeverity(3);
      setError(null);
      fetchActivities();
    }
  }, [isOpen]);

  // When the date changes, auto-check the matching weekday so it's included
  // in weekly repeat days (the day matching the selected date is locked).
  useEffect(() => {
    if (isOpen && date) {
      const weekday = getWeekdayFromDate(date);
      if (weekday) {
        setRepeatDays(prev => (prev.includes(weekday) ? prev : [...prev, weekday]));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, isOpen]);

  const goToStep = (step) => {
    setStepTransition('exiting');
    setTimeout(() => {
      setCurrentStep(step);
      setStepTransition('entering');
    }, 150);
  };

  const handleStepClick = (targetStep) => {
    // Allow going back
    if (targetStep < currentStep) {
      goToStep(targetStep);
      return;
    }
    if (targetStep === currentStep) return;

    // Going forward
    if (targetStep > currentStep + 1) {
      setError('Morate završiti trenutni korak pre nego što nastavite dalje.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Step 1 requires a selected activity
    if (targetStep === 1 && !selectedActivity) {
      setError('Prvo morate izabrati aktivnost.');
      setTimeout(() => setError(null), 3000);
      return;
    }
    // Step 2 requires a selected activity and a date
    if (targetStep === 2 && (!selectedActivity || !date)) {
      setError('Prvo morate izabrati aktivnost i datum.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    goToStep(targetStep);
  };

  const handleActivitySelect = (activity) => {
    if (isTransitioning) return;
    const isDeselecting = selectedActivity?.id === activity.id;
    setSelectedActivity(isDeselecting ? null : activity);
    if (isDeselecting) {
      if (stepTransitionTimeout.current) {
        clearTimeout(stepTransitionTimeout.current);
        stepTransitionTimeout.current = null;
      }
      return;
    }
    if (activity.duration) {
      const [h, m] = startTime.split(':').map(Number);
      const totalMins = h * 60 + m + activity.duration;
      setEndTime(`${String(Math.floor(totalMins / 60) % 24).padStart(2, '0')}:${String(totalMins % 60).padStart(2, '0')}`);
    }
    // Auto-advance to the next step after a short delay
    stepTransitionTimeout.current = setTimeout(() => {
      setIsTransitioning(true);
      setStepTransition('exiting');
      setTimeout(() => {
        setCurrentStep(1);
        setStepTransition('entering');
        setIsTransitioning(false);
      }, 150);
    }, 1000);
  };

  const nextPage = () => {
    const totalPages = Math.ceil(filteredActivities.length / pageSize);
    if (page < totalPages) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev + 1);
        setPageFading(false);
      }, 300);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      setPageFading(true);
      setTimeout(() => {
        setPage(prev => prev - 1);
        setPageFading(false);
      }, 300);
    }
  };

  const handleSubmit = async () => {
    if (!selectedActivity || !date) return;
    setIsSaving(true);
    setError(null);
    try {
      const [yr, mo, dy] = date.split('-').map(Number);
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      const startDT = new Date(yr, mo - 1, dy, sh, sm, 0, 0);
      const endDT = new Date(yr, mo - 1, dy, eh, em, 0, 0);
      // Build repeat payload: weekly sends selected days, daily overrides with empty list
      let repeatDaysPayload = [];
      if (repeatFrequency === 'weekly') {
        repeatDaysPayload = repeatDays;
      } else if (repeatFrequency === 'daily') {
        repeatDaysPayload = [];
      }

      await axiosInstance.post(`/api/Group/${group.id}/activities`, {
        name: selectedActivity.name,
        date, // plain yyyy-MM-dd (DateOnly) — no timezone shift
        startTime: startDT.toISOString(),
        endTime: endDT.toISOString(),
        activityType: selectedActivity.activityType,
        notes,
        description: notes || null,
        repeatFrequency,
        repeatDays: repeatDaysPayload,
        taskCreation,
        severity
      });
      onSuccess?.();
      onClose();
    } catch {
      setError('Greška pri dodavanju aktivnosti. Pokušajte ponovo.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredActivities = activities.filter(act =>
    !searchTerm || act.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredActivities.length / pageSize);
  const start = (page - 1) * pageSize;
  const paginatedActivities = filteredActivities.slice(start, start + pageSize);

  const inputStyle = {
    width: '100%',
    height: '2.5rem',
    padding: '0 1rem',
    border: '1px solid var(--cds-border-strong-01)',
    backgroundColor: 'var(--cds-field-01)',
    color: 'var(--cds-text-primary)',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  const steps = [
    { label: 'Aktivnost', description: selectedActivity ? selectedActivity.name : '' },
    { label: 'Detalji', description: '' },
    { label: 'Potvrda', description: '' }
  ];

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar add-activity-sidebar ${isOpen ? 'open' : ''}`} style={{ width: '440px' }}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>Dodaj aktivnost</h3>
          <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly iconDescription="Zatvori" onClick={onClose} />
        </div>

        {/* Progress Indicator */}
        <div style={{
          padding: '1rem 1.5rem 0.75rem',
          borderBottom: '1px solid var(--cds-border-subtle)',
          backgroundColor: 'var(--cds-layer-01)'
        }}>
          <div className="custom-progress-bar">
            <div className="progress-steps">
              {steps.map((step, idx) => (
                <React.Fragment key={idx}>
                  <div
                    className={`progress-step ${currentStep >= idx ? 'completed' : ''} ${currentStep === idx ? 'active' : ''}`}
                    onClick={() => handleStepClick(idx)}
                    style={{ cursor: idx <= currentStep ? 'pointer' : 'default' }}
                  >
                    <div className="step-circle">
                      {currentStep > idx ? <Checkmark size={16} /> : idx + 1}
                    </div>
                    <div className="step-label">
                      <div className="step-title">{step.label}</div>
                      {currentStep > idx && step.description && (
                        <div className="step-description">{step.description}</div>
                      )}
                    </div>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`progress-line ${currentStep > idx ? 'completed' : ''}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        <div className="edit-sidebar__content">
          {error && (
            <div style={{ color: '#da1e28', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(218,30,40,0.08)', borderRadius: '4px', borderLeft: '3px solid #da1e28', marginBottom: '0.5rem' }}>
              {error}
            </div>
          )}

          {/* Step 0 - Activity selection */}
          {currentStep === 0 && (
            <div className={`step-content ${stepTransition}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Search
                  size="md"
                  placeholder="Pretraži aktivnosti..."
                  labelText="Pretraga"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                  onClear={() => { setSearchTerm(''); setPage(1); }}
                  style={{ flex: 1 }}
                />
                {filteredActivities.length > pageSize && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Button
                      kind="ghost"
                      size="md"
                      renderIcon={ChevronLeft}
                      onClick={prevPage}
                      hasIconOnly
                      disabled={page === 1}
                    />
                    <span style={{ fontSize: '0.875rem', opacity: 0.8, minWidth: '60px', textAlign: 'center', fontWeight: '500' }}>
                      {page}/{totalPages}
                    </span>
                    <Button
                      kind="ghost"
                      size="md"
                      renderIcon={ChevronRight}
                      onClick={nextPage}
                      hasIconOnly
                      disabled={page === totalPages}
                    />
                  </div>
                )}
              </div>

              {activitiesLoading ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                  {[...Array(6)].map((_, idx) => (
                    <SkeletonPlaceholder key={idx} style={{ height: '80px', borderRadius: '6px' }} />
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '0.5rem',
                    opacity: pageFading ? 0 : 1,
                    transition: 'opacity 0.3s ease'
                  }}
                >
                  {filteredActivities.length === 0 ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem 1rem', color: 'var(--cds-text-secondary)' }}>
                      {searchTerm ? 'Nema aktivnosti koje odgovaraju pretrazi' : 'Nema dostupnih aktivnosti'}
                    </div>
                  ) : (
                    paginatedActivities.map(act => {
                      const isSelected = selectedActivity?.id === act.id;
                      const typeColor = getActivityTypeColor(act.activityType);
                      return (
                        <div
                          key={act.id}
                          onClick={() => handleActivitySelect(act)}
                          style={{
                            padding: '0.625rem',
                            borderRadius: '6px',
                            border: `2px solid ${isSelected ? '#9fb746' : 'var(--cds-border-subtle-00, rgba(0,0,0,0.08))'}`,
                            borderLeft: `4px solid ${typeColor}`,
                            backgroundColor: isSelected ? 'rgba(159,183,70,0.1)' : 'var(--cds-layer-02)',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            position: 'relative'
                          }}
                        >
                          <div style={{ fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.25rem', paddingRight: isSelected ? '1.25rem' : 0 }}>
                            {act.name}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.65rem', color: typeColor, fontWeight: '500' }}>{translateActivityType(act.activityType)}</span>
                            {act.duration && (
                              <span style={{ fontSize: '0.65rem', color: 'var(--cds-text-secondary)' }}>{act.duration} min</span>
                            )}
                          </div>
                          {isSelected && (
                            <span style={{ position: 'absolute', top: '4px', right: '6px', color: '#9fb746', fontWeight: '700', fontSize: '0.875rem' }}>✓</span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                <Button kind="secondary" onClick={onClose} style={{ flex: 1 }}>Otkaži</Button>
                <Button kind="primary" onClick={() => handleStepClick(1)} style={{ flex: 1 }} disabled={!selectedActivity}>Dalje</Button>
              </div>
            </div>
          )}

          {/* Step 1 - Details (date, time, repeat, notes) */}
          {currentStep === 1 && (
            <div className={`step-content ${stepTransition}`}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Detalji aktivnosti</h3>

              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                  Datum *
                </label>
                <input type="date" value={date} onChange={e => handleDateChange(e.target.value)} style={inputStyle} required />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', minWidth: 0 }}>
                <div style={{ minWidth: 0 }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                    Vreme početka *
                  </label>
                  <div style={{ display: 'flex', gap: '0.25rem', minWidth: 0 }}>
                    <Select
                      id="start-hour"
                      labelText=""
                      hideLabel
                      value={startTime.split(':')[0]}
                      onChange={e => {
                        const newHour = e.target.value;
                        setStartTime(`${newHour}:${startTime.split(':')[1]}`);
                        // Automatically move the end hour to match the new start hour
                        setEndTime(`${newHour}:${endTime.split(':')[1]}`);
                      }}
                      disabled={isSaving}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </Select>
                    <Select
                      id="start-minute"
                      labelText=""
                      hideLabel
                      value={startTime.split(':')[1]}
                      onChange={e => setStartTime(`${startTime.split(':')[0]}:${e.target.value}`)}
                      disabled={isSaving}
                    >
                      {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                        <SelectItem key={m} value={m} text={m} />
                      ))}
                    </Select>
                  </div>
                </div>
                <div style={{ minWidth: 0 }}>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                    Vreme kraja
                  </label>
                  <div style={{ display: 'flex', gap: '0.25rem', minWidth: 0 }}>
                    <Select
                      id="end-hour"
                      labelText=""
                      hideLabel
                      value={endTime.split(':')[0]}
                      onChange={e => setEndTime(`${e.target.value}:${endTime.split(':')[1]}`)}
                      disabled={isSaving}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <SelectItem key={i} value={String(i).padStart(2, '0')} text={String(i).padStart(2, '0')} />
                      ))}
                    </Select>
                    <Select
                      id="end-minute"
                      labelText=""
                      hideLabel
                      value={endTime.split(':')[1]}
                      onChange={e => setEndTime(`${endTime.split(':')[0]}:${e.target.value}`)}
                      disabled={isSaving}
                    >
                      {['00','05','10','15','20','25','30','35','40','45','50','55'].map(m => (
                        <SelectItem key={m} value={m} text={m} />
                      ))}
                    </Select>
                  </div>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                  Ponavljanje
                </label>
                <Select
                  id="act-repeat-frequency"
                  labelText=""
                  hideLabel
                  value={repeatFrequency}
                  onChange={e => {
                    const newFreq = e.target.value;
                    setRepeatFrequency(newFreq);
                    // When switching to weekly, auto-add the weekday matching the selected date
                    if (newFreq === 'weekly') {
                      const weekday = getWeekdayFromDate(date);
                      if (weekday) {
                        setRepeatDays(prev => (prev.includes(weekday) ? prev : [...prev, weekday]));
                      }
                    }
                  }}
                  disabled={isSaving}
                >
                  <SelectItem value="none" text="Ne ponavlja se" />
                  <SelectItem value="daily" text="Svakodnevno" />
                  <SelectItem value="weekly" text="Nedeljno" />
                </Select>
              </div>

              <div>
                <Toggle
                  id="act-task-creation"
                  labelText="Kreiraj Zadatak"
                  labelA="Ne"
                  labelB="Da"
                  toggled={taskCreation}
                  onToggle={(checked) => setTaskCreation(checked)}
                  disabled={isSaving}
                />
                {taskCreation && (
                  <div style={{ marginTop: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                      Nivo ozbiljnosti (severity)
                    </label>
                    <Select
                      id="act-severity"
                      labelText=""
                      hideLabel
                      value={severity}
                      onChange={e => setSeverity(Number(e.target.value))}
                      disabled={isSaving}
                    >
                      <SelectItem value={1} text="1 - Veoma važno" />
                      <SelectItem value={2} text="2 - Važno" />
                      <SelectItem value={3} text="3 - Normalno" />
                    </Select>
                  </div>
                )}
              </div>

              {repeatFrequency === 'weekly' && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.75rem', fontWeight: '400', color: 'var(--cds-text-secondary, #525252)' }}>
                    Dani u nedelji
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    {WEEKDAYS.map(day => {
                      const isLocked = day.value === getWeekdayFromDate(date);
                      const isChecked = repeatDays.includes(day.value);
                      return (
                        <Checkbox
                          key={day.value}
                          id={`repeat-day-${day.value}`}
                          labelText={day.label}
                          checked={isChecked}
                          disabled={isLocked}
                          onChange={() => toggleRepeatDay(day.value)}
                        />
                      );
                    })}
                  </div>
                  <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: 'var(--cds-text-secondary, #525252)' }}>
                    Dan koji odgovara izabranom datumu je automatski označen i ne može se isključiti dok ne promenite datum.
                  </p>
                </div>
              )}

              <TextArea
                id="act-notes"
                labelText="Napomena (opciono)"
                placeholder="Unesite napomenu..."
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                disabled={isSaving}
              />

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button kind="secondary" onClick={() => goToStep(0)} style={{ flex: 1 }} disabled={isSaving}>Nazad</Button>
                <Button kind="primary" onClick={() => goToStep(2)} style={{ flex: 1 }} disabled={isSaving || !date}>Dalje</Button>
              </div>
            </div>
          )}

          {/* Step 2 - Confirmation */}
          {currentStep === 2 && (
            <div className={`step-content ${stepTransition}`}>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Potvrdi dodavanje</h3>
              <div style={{ padding: '1rem', backgroundColor: 'var(--cds-layer-02)', borderRadius: '8px' }}>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Grupa:</strong> {group?.name}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Aktivnost:</strong> {selectedActivity?.name}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Tip aktivnosti:</strong> {selectedActivity ? translateActivityType(selectedActivity.activityType) : '-'}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Datum:</strong> {date}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Vreme:</strong> {startTime} - {endTime}
                </p>
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Ponavljanje:</strong> {repeatFrequency === 'none' ? 'Ne ponavlja se' : repeatFrequency === 'daily' ? 'Svakodnevno' : repeatFrequency === 'weekly' ? 'Nedeljno' : repeatFrequency}
                </p>
                {repeatFrequency === 'weekly' && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Dani:</strong> {repeatDays.length > 0
                      ? repeatDays.map(d => WEEKDAYS.find(w => w.value === d)?.label).filter(Boolean).join(', ')
                      : '-'}
                  </p>
                )}
                <p style={{ marginBottom: '0.5rem' }}>
                  <strong>Kreiraj Zadatak:</strong> {taskCreation ? 'Da' : 'Ne'}
                </p>
                {taskCreation && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    <strong>Nivo ozbiljnosti:</strong> {severity}
                  </p>
                )}
                {notes && (
                  <p style={{ marginBottom: 0 }}>
                    <strong>Napomena:</strong> {notes}
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '1rem' }}>
                <Button kind="secondary" onClick={() => goToStep(1)} style={{ flex: 1 }} disabled={isSaving}>Nazad</Button>
                <Button kind="primary" onClick={handleSubmit} style={{ flex: 1 }} disabled={isSaving || !selectedActivity || !date}>
                  {isSaving ? 'Čuvanje...' : 'Potvrdi'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ===========================================
// Delete Group Sidebar Component
// (Right side panel for deleting a group, 2-step confirmation)
// ===========================================
const DeleteGroupSidebar = ({ isOpen, onClose, group, residentCount, onSuccess }) => {
  const [step, setStep] = useState(0); // 0 = warning, 1 = confirm
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setStep(0);
      setError(null);
    }
  }, [isOpen]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleDelete = async () => {
    if (!group) return;

    setIsSaving(true);
    setError(null);
    try {
      await axiosInstance.delete(`/api/Group/${group.id}`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Greška pri brisanju grupe:', err);
      setError(err.response?.data?.message || 'Došlo je do greške pri brisanju grupe');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar add-activity-sidebar ${isOpen ? 'open' : ''}`} style={{ width: '440px' }}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>
            {step === 0 ? 'Obriši grupu' : 'Potvrda brisanja grupe'}
          </h3>
          <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly iconDescription="Zatvori" onClick={onClose} />
        </div>

        <div className="edit-sidebar__content">
          {step === 0 ? (
            <div className="step-content">
              {/* Warning summary */}
              <div style={{
                padding: '1rem',
                backgroundColor: 'rgba(218, 30, 40, 0.08)',
                border: '1px solid rgba(218, 30, 40, 0.3)',
                borderLeft: '4px solid #da1e28',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.9375rem', fontWeight: '600', color: '#da1e28' }}>
                  ⚠️ Ova akcija je trajna i ne može se poništiti!
                </p>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  Brisanjem grupe <strong>{group?.name}</strong>:
                </p>
                <ul style={{ margin: '0 0 0.5rem', paddingLeft: '1.25rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <li>Svi korisnici se <strong>izbacuju iz ove grupe</strong></li>
                  <li>Svim korisnicima se <strong>brišu grupne aktivnosti</strong></li>
                  <li>Grupa se <strong>trajno briše</strong> iz sistema</li>
                </ul>
                <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--cds-text-secondary, #525252)' }}>
                  Ova akcija se ne može poništiti.
                </p>
              </div>

              {error && (
                <div style={{ color: '#da1e28', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(218,30,40,0.08)', borderRadius: '4px', borderLeft: '3px solid #da1e28' }}>
                  {error}
                </div>
              )}

              <div className="edit-sidebar__actions">
                <Button kind="secondary" onClick={onClose} disabled={isSaving}>Otkaži</Button>
                <Button kind="danger" onClick={() => setStep(1)} disabled={isSaving}>Dalje</Button>
              </div>
            </div>
          ) : (
            <div className="step-content">
              {/* Summary */}
              <div style={{
                padding: '1rem',
                backgroundColor: 'var(--cds-layer-02, #f4f4f4)',
                borderRadius: '8px'
              }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.875rem', fontWeight: '600', color: 'var(--cds-text-primary, #161616)' }}>
                  Pregled brisanja
                </p>
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <strong>Grupa:</strong> {group?.name}
                </p>
                <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <strong>Broj korisnika:</strong> {residentCount ?? 0}
                </p>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                  <strong>Posledice:</strong> Svi korisnici se izbacuju iz grupe i brišu im se grupne aktivnosti.
                </p>
              </div>

              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--cds-text-primary, #161616)' }}>
                Klikom na <strong>Potvrdi brisanje</strong> grupa <strong>{group?.name}</strong> će biti trajno obrisana.
              </p>

              {error && (
                <div style={{ color: '#da1e28', fontSize: '0.8125rem', padding: '0.5rem 0.75rem', backgroundColor: 'rgba(218,30,40,0.08)', borderRadius: '4px', borderLeft: '3px solid #da1e28' }}>
                  {error}
                </div>
              )}

              <div className="edit-sidebar__actions">
                <Button kind="secondary" onClick={() => setStep(0)} disabled={isSaving}>Nazad</Button>
                <Button kind="danger" onClick={handleDelete} disabled={isSaving}>
                  {isSaving ? 'Brisanje...' : 'Potvrdi brisanje'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ===========================================
// Group Activities Sidebar Component
// (Opens when "Aktivnosti" is clicked)
// Calendar-style, mobile-friendly
// ===========================================
const GroupActivitiesSidebar = ({ isOpen, onClose, group, onAddActivity, canManageActivities = true }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewDate, setViewDate] = useState(new Date());
  const [selectedActivityIds, setSelectedActivityIds] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const toggleActivitySelect = (id) => {
    setSelectedActivityIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Označi/poništi sve aktivnosti za trenutno prikazani mesec (toggle).
  const toggleSelectAllMonth = (monthActivityIds) => {
    if (!monthActivityIds || monthActivityIds.length === 0) return;
    setSelectedActivityIds((prev) => {
      const allSelected = monthActivityIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !monthActivityIds.includes(id));
      }
      const merged = new Set(prev);
      monthActivityIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  };

  const handleDeleteActivities = async () => {
    if (selectedActivityIds.length === 0 || !group?.id) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await axiosInstance.post(`/api/Group/${group.id}/activities/delete`, {
        activityIds: selectedActivityIds
      });
      setIsDeleteModalOpen(false);
      setSelectedActivityIds([]);
      // Refresh activities
      const response = await axiosInstance.get(`/api/Group/${group.id}/activities`);
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (response.data.success && Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data.$values)) {
          data = response.data.$values;
        } else if (Array.isArray(response.data.value)) {
          data = response.data.value;
        } else if (Array.isArray(response.data.items)) {
          data = response.data.items;
        }
      }
      setActivities(data);
    } catch (err) {
      console.error('Error deleting group activities:', err);
      setDeleteError(err.response?.data?.message || 'Greška pri brisanju aktivnosti');
    } finally {
      setIsDeleting(false);
    }
  };

  // Fetch activities for this group when the sidebar opens
  useEffect(() => {
    if (isOpen && group?.id) {
      const fetchActivities = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await axiosInstance.get(`/api/Group/${group.id}/activities`);
          console.log('Group activities API Response:', response);
          let data = [];
          if (Array.isArray(response.data)) {
            data = response.data;
          } else if (response.data && typeof response.data === 'object') {
            if (response.data.success && Array.isArray(response.data.data)) {
              data = response.data.data;
            } else if (Array.isArray(response.data.data)) {
              data = response.data.data;
            } else if (Array.isArray(response.data.$values)) {
              data = response.data.$values;
            } else if (Array.isArray(response.data.value)) {
              data = response.data.value;
            } else if (Array.isArray(response.data.items)) {
              data = response.data.items;
            }
          }
          setActivities(data);
        } catch (err) {
          console.error('Error fetching group activities:', err);
          setError('Greška pri učitavanju aktivnosti');
          setActivities([]);
        } finally {
          setLoading(false);
        }
      };
      fetchActivities();
    } else if (!isOpen) {
      setActivities([]);
      setError(null);
    }
  }, [isOpen, group?.id]);

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const date = new Date(timeStr);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('sr-Latn-RS', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed': return 'Završeno';
      case 'planned': return 'Planirano';
      case 'in-progress': case 'inprogress': return 'U toku';
      case 'cancelled': return 'Otkazano';
      default: return status || '-';
    }
  };

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'completed': return '#198038';
      case 'planned': return '#0f62fe';
      case 'in-progress': case 'inprogress': return '#f1c21b';
      case 'cancelled': return '#da1e28';
      default: return '#6f6f6f';
    }
  };

  // Group activities by date (yyyy-MM-dd)
  const groupByDate = () => {
    const map = {};
    activities.forEach((activity) => {
      if (!activity.date) return;
      const key = activity.date.slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(activity);
    });
    return map;
  };

  const grouped = groupByDate();

  // Sort dates ascending
  const sortedDates = Object.keys(grouped).sort((a, b) => new Date(a) - new Date(b));

  // Filter dates to the currently viewed month
  const viewMonth = viewDate.getMonth();
  const viewYear = viewDate.getFullYear();
  const visibleDates = sortedDates.filter((d) => {
    const dt = new Date(d);
    return dt.getMonth() === viewMonth && dt.getFullYear() === viewYear;
  });

  // Svi ID-jevi aktivnosti za trenutno prikazani mesec (za "Označi sve").
  const monthActivityIds = visibleDates.flatMap((dateKey) =>
    (grouped[dateKey] || []).map((activity) => activity.id)
  );
  const allMonthSelected =
    monthActivityIds.length > 0 && monthActivityIds.every((id) => selectedActivityIds.includes(id));

  const monthNames = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];

  const navigateMonth = (dir) => {
    setViewDate((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() + dir, 1);
      return next;
    });
  };

  const formatDayLabel = (dateStr) => {
    const dt = new Date(dateStr);
    const days = ['Nedelja', 'Ponedeljak', 'Utorak', 'Sreda', 'Četvrtak', 'Petak', 'Subota'];
    return `${days[dt.getDay()]}, ${dt.getDate()}. ${monthNames[dt.getMonth()]}`;
  };

  const isToday = (dateStr) => {
    const today = new Date();
    return dateStr === `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  };

  return (
    <>
      {isOpen && <div className="edit-sidebar-overlay" onClick={onClose} />}
      <div className={`edit-sidebar add-activity-sidebar ${isOpen ? 'open' : ''}`} style={{ width: '440px' }}>
        <div className="edit-sidebar__header">
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '500' }}>Aktivnosti</h3>
          <Button kind="ghost" size="sm" renderIcon={Close} hasIconOnly iconDescription="Zatvori" onClick={onClose} />
        </div>

        <div className="edit-sidebar__content">
          <div className="group-activities-content" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Header row: action buttons */}
            {canManageActivities && (
            <div className="group-activities-actions">
              <Button
                kind="primary"
                size="sm"
                renderIcon={Add}
                onClick={onAddActivity}
                className="group-add-activity-toggle"
              >
                Dodaj aktivnost
              </Button>
              <Button
                kind="danger"
                size="sm"
                renderIcon={TrashCan}
                onClick={() => {
                  setDeleteError(null);
                  setIsDeleteModalOpen(true);
                }}
                className="group-delete-activity-toggle"
                disabled={selectedActivityIds.length === 0}
              >
                Obriši aktivnost
              </Button>
            </div>
            )}

            {error && (
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={error}
                onCloseButtonClick={() => setError(null)}
                hideCloseButton={false}
                lowContrast
              />
            )}

            {loading && (
              <div className="group-residents-loading">
                <Loading description="Učitavanje aktivnosti..." withOverlay={false} small />
              </div>
            )}

            {!loading && !error && activities.length === 0 && (
              <div className="group-residents-empty">
                <Activity size={24} />
                <span>Nema aktivnosti za ovu grupu</span>
              </div>
            )}

            {!loading && !error && activities.length > 0 && (
              <>
                {/* Calendar month navigation */}
                <div className="group-calendar-nav">
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={ChevronLeft}
                    onClick={() => navigateMonth(-1)}
                    hasIconOnly
                    iconDescription="Prethodni mesec"
                    className="group-calendar-nav-arrow"
                  />
                  <div className="group-calendar-nav-center">
                    <span className="group-calendar-nav-month">{monthNames[viewMonth]} {viewYear}</span>
                  </div>
                  <Button
                    kind="ghost"
                    size="sm"
                    renderIcon={ChevronRight}
                    onClick={() => navigateMonth(1)}
                    hasIconOnly
                    iconDescription="Sledeći mesec"
                    className="group-calendar-nav-arrow"
                  />
                </div>

                {/* Calendar content */}
                {visibleDates.length === 0 ? (
                  <div className="group-residents-empty">
                    <Activity size={24} />
                    <span>Nema aktivnosti u {monthNames[viewMonth]} {viewYear}</span>
                  </div>
                ) : (
                  <div className="group-calendar-list">
                    {visibleDates.map((dateKey) => (
                      <div key={dateKey} className={`group-calendar-day ${isToday(dateKey) ? 'group-calendar-day--today' : ''}`}>
                        <div className="group-calendar-day-header">
                          <span className="group-calendar-day-label">{formatDayLabel(dateKey)}</span>
                          {isToday(dateKey) && <span className="group-calendar-day-today-badge">Danas</span>}
                        </div>
                        <div className="group-calendar-day-activities">
                          {grouped[dateKey].map((activity) => {
                            const typeColor = getActivityTypeColor(activity.activityType);
                            const isSelected = selectedActivityIds.includes(activity.id);
                            return (
                              <div
                                key={activity.id}
                                className={`group-calendar-activity ${isSelected ? 'group-calendar-activity--selected' : ''}`}
                                onClick={() => toggleActivitySelect(activity.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    toggleActivitySelect(activity.id);
                                  }
                                }}
                              >
                                <div className="group-calendar-activity-check">
                                  <Checkbox
                                    id={`activity-check-${activity.id}`}
                                    labelText=""
                                    checked={isSelected}
                                    onChange={() => toggleActivitySelect(activity.id)}
                                    hideLabel
                                  />
                                </div>
                                <div className="group-calendar-activity-time">
                                  {formatTime(activity.startTime)}
                                </div>
                                <div className="group-calendar-activity-body">
                                  <div className="group-calendar-activity-name">{activity.name}</div>
                                  <div className="group-calendar-activity-meta">
                                    <span className="group-calendar-activity-type" style={{ color: typeColor }}>
                                      {translateActivityType(activity.activityType)}
                                    </span>
                                    <span className="group-calendar-activity-status" style={{ color: getStatusColor(activity.status), backgroundColor: `${getStatusColor(activity.status)}1a` }}>
                                      {getStatusLabel(activity.status)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Floating "Označi sve" — selects every activity of the viewed month */}
        {!loading && !error && monthActivityIds.length > 0 && (
          <button
            type="button"
            className="group-select-all-fab"
            onClick={() => toggleSelectAllMonth(monthActivityIds)}
            aria-label={allMonthSelected ? 'Poništi izbor' : 'Označi sve'}
          >
            <CheckboxChecked size={20} aria-hidden="true" />
            <span>{allMonthSelected ? 'Poništi sve' : 'Označi sve'}</span>
          </button>
        )}
      </div>

      {/* Delete Activities Confirmation Modal - outside sidebar to avoid transform issue */}
      <Modal
        open={isDeleteModalOpen}
        onRequestClose={() => setIsDeleteModalOpen(false)}
        modalHeading="Obriši aktivnosti"
        primaryButtonText={isDeleting ? 'Brisanje...' : 'Obriši'}
        secondaryButtonText="Otkaži"
        primaryButtonDisabled={isDeleting}
        onRequestSubmit={handleDeleteActivities}
        danger
        size="sm"
      >
        <p>
          Da li ste sigurni da želite da obrišete{' '}
          <strong>{selectedActivityIds.length}</strong> označenu aktivnost(i) iz grupe{' '}
          <strong>{group?.name}</strong>?
        </p>
        <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
          Ova akcija se ne može poništiti.
        </p>
        {deleteError && (
          <div style={{ marginTop: '0.75rem', color: '#da1e28', fontSize: '0.8125rem' }}>
            {deleteError}
          </div>
        )}
      </Modal>
    </>
  );
};

const Groups = ({ embedded = false }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';
  const navigate = useNavigate();
  const { can, isAdmin } = usePermissions();

  // Akcione kontrole se prikazuju samo ako korisnik ima odgovarajuću dozvolu.
  // Administrator ima sve dozvole (isAdmin fallback).
  const canManageGroups = isAdmin || can('groups.manage');
  const canManageMembers = isAdmin || can('groups.members.manage');
  const canManageActivities = isAdmin || can('groups.activities.manage');

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(5);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Side panel state
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // Detail side panel state
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [detailGroup, setDetailGroup] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Group residents state
  const [groupResidents, setGroupResidents] = useState([]);
  const [groupResidentsLoading, setGroupResidentsLoading] = useState(false);
  const [groupResidentsError, setGroupResidentsError] = useState(null);
  const [residentsPage, setResidentsPage] = useState(1);
  const [residentsPageSize] = useState(5);
  const [residentsPagination, setResidentsPagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Add resident to group state
  const [availableResidents, setAvailableResidents] = useState([]);
  const [availableResidentsLoading, setAvailableResidentsLoading] = useState(false);
  const [availableResidentsError, setAvailableResidentsError] = useState(null);
  const [selectedResidentId, setSelectedResidentId] = useState('');
  const [selectedResident, setSelectedResident] = useState(null);
  const [addResidentLoading, setAddResidentLoading] = useState(false);
  const [addResidentError, setAddResidentError] = useState(null);
  const [isAddResidentOpen, setIsAddResidentOpen] = useState(false);
  const [addStep, setAddStep] = useState(0);
  const [addStepTransition, setAddStepTransition] = useState('entering');
  const [availablePage, setAvailablePage] = useState(1);
  const [availablePageSize] = useState(5);
  const [availablePagination, setAvailablePagination] = useState({
    totalCount: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Remove resident from group state
  const [residentToRemove, setResidentToRemove] = useState(null);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [removeResidentLoading, setRemoveResidentLoading] = useState(false);
  const [removeResidentError, setRemoveResidentError] = useState(null);

  // Delete group state
  const [isDeleteGroupSidebarOpen, setIsDeleteGroupSidebarOpen] = useState(false);

  // Add activity to group sidebar state
  const [isAddActivityOpen, setIsAddActivityOpen] = useState(false);
  // Activities sidebar state (opens when "Dodaj aktivnosti" is clicked)
  const [isActivitiesSidebarOpen, setIsActivitiesSidebarOpen] = useState(false);
  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });

  useEffect(() => {
    fetchGroups();
  }, [page, searchTerm]);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('pageNumber', String(page));
      params.append('pageSize', String(pageSize));
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await axiosInstance.get(`/api/Group?${params.toString()}`);
      console.log('API Response:', response);

      if (response.data.success && Array.isArray(response.data.data)) {
        setGroups(response.data.data);
        const paginationData = response.data.pagination;
        if (paginationData) {
          setPagination({
            totalCount: paginationData.totalCount || 0,
            totalPages: paginationData.totalPages || 1,
            hasNextPage: paginationData.hasNextPage || false,
            hasPreviousPage: paginationData.hasPreviousPage || false
          });
        } else {
          setPagination({
            totalCount: response.data.data.length,
            totalPages: Math.ceil(response.data.data.length / pageSize) || 1,
            hasNextPage: page < Math.ceil(response.data.data.length / pageSize),
            hasPreviousPage: page > 1
          });
        }
      } else {
        setError('Neispravan format podataka');
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Greška pri učitavanju grupa');
    } finally {
      setLoading(false);
    }
  };

  const nextPage = () => {
    if (pagination.hasNextPage) {
      setPage(prev => prev + 1);
    }
  };

  const prevPage = () => {
    if (pagination.hasPreviousPage) {
      setPage(prev => prev - 1);
    }
  };

  const openSidePanel = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setFormData({
        name: group.name || '',
        description: group.description || ''
      });
    } else {
      setEditingGroup(null);
      setFormData({
        name: '',
        description: ''
      });
    }
    setFormError(null);
    setIsSidePanelOpen(true);
  };

  const openGroupDetail = async (group) => {
    setDetailGroup(null);
    setDetailError(null);
    setDetailLoading(true);
    setIsDetailPanelOpen(true);

    try {
      const response = await axiosInstance.get(`/api/Group/${group.id}`);
      if (response.data.success && response.data.data) {
        setDetailGroup(response.data.data);
      } else {
        setDetailError('Neispravan format podataka');
      }
    } catch (error) {
      console.error('Error fetching group detail:', error);
      setDetailError(error.response?.data?.message || 'Greška pri učitavanju grupe');
    } finally {
      setDetailLoading(false);
    }

    // Fetch residents in this group
    setResidentsPage(1);
    fetchGroupResidents(group.id, 1);
    // Fetch available residents for adding
    setAvailablePage(1);
    fetchAvailableResidents(group.id, 1);
  };

  const closeGroupDetail = () => {
    setIsDetailPanelOpen(false);
    setDetailGroup(null);
    setDetailError(null);
    setGroupResidents([]);
    setGroupResidentsError(null);
    setSelectedResidentId('');
    setSelectedResident(null);
    setAddResidentError(null);
    setIsAddResidentOpen(false);
    setAddStep(0);
    setResidentsPage(1);
    setIsAddActivityOpen(false);
    setIsActivitiesSidebarOpen(false);
  };

  const fetchGroupResidents = async (groupId, page = residentsPage) => {
    try {
      setGroupResidentsLoading(true);
      setGroupResidentsError(null);
      const params = new URLSearchParams();
      params.append('pageNumber', String(page));
      params.append('pageSize', String(residentsPageSize));
      const response = await axiosInstance.get(`/api/Group/${groupId}/residents?${params.toString()}`);
      console.log('Group residents API Response:', response);

      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data.$values)) {
          data = response.data.$values;
        } else if (Array.isArray(response.data.value)) {
          data = response.data.value;
        } else if (Array.isArray(response.data.items)) {
          data = response.data.items;
        }
      }
      setGroupResidents(data);

      // Pagination metadata
      const pagination = response.data?.pagination;
      if (pagination) {
        setResidentsPagination({
          totalCount: pagination.totalCount || 0,
          totalPages: pagination.totalPages || 1,
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false
        });
      } else {
        setResidentsPagination({
          totalCount: data.length,
          totalPages: Math.ceil(data.length / residentsPageSize) || 1,
          hasNextPage: page < Math.ceil(data.length / residentsPageSize),
          hasPreviousPage: page > 1
        });
      }
    } catch (error) {
      console.error('Error fetching group residents:', error);
      setGroupResidentsError(error.response?.data?.message || 'Greška pri učitavanju korisnika grupe');
    } finally {
      setGroupResidentsLoading(false);
    }
  };

  const nextResidentsPage = () => {
    if (residentsPagination.hasNextPage) {
      const next = residentsPage + 1;
      setResidentsPage(next);
      fetchGroupResidents(detailGroup.id, next);
    }
  };

  const prevResidentsPage = () => {
    if (residentsPagination.hasPreviousPage) {
      const prev = residentsPage - 1;
      setResidentsPage(prev);
      fetchGroupResidents(detailGroup.id, prev);
    }
  };

  const fetchAvailableResidents = async (groupId, page = availablePage) => {
    try {
      setAvailableResidentsLoading(true);
      setAvailableResidentsError(null);
      const params = new URLSearchParams();
      params.append('pageNumber', String(page));
      params.append('pageSize', String(availablePageSize));
      const response = await axiosInstance.get(`/api/Group/${groupId}/residents/not-in-group?${params.toString()}`);
      console.log('Available residents API Response:', response);

      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (Array.isArray(response.data.data)) {
          data = response.data.data;
        } else if (Array.isArray(response.data.$values)) {
          data = response.data.$values;
        } else if (Array.isArray(response.data.value)) {
          data = response.data.value;
        } else if (Array.isArray(response.data.items)) {
          data = response.data.items;
        }
      }
      setAvailableResidents(data);

      // Pagination metadata
      const pagination = response.data?.pagination;
      if (pagination) {
        setAvailablePagination({
          totalCount: pagination.totalCount || 0,
          totalPages: pagination.totalPages || 1,
          hasNextPage: pagination.hasNextPage || false,
          hasPreviousPage: pagination.hasPreviousPage || false
        });
      } else {
        setAvailablePagination({
          totalCount: data.length,
          totalPages: Math.ceil(data.length / availablePageSize) || 1,
          hasNextPage: page < Math.ceil(data.length / availablePageSize),
          hasPreviousPage: page > 1
        });
      }
    } catch (error) {
      console.error('Error fetching available residents:', error);
      setAvailableResidentsError(error.response?.data?.message || 'Greška pri učitavanju korisnika');
    } finally {
      setAvailableResidentsLoading(false);
    }
  };

  const nextAvailablePage = () => {
    if (availablePagination.hasNextPage) {
      const next = availablePage + 1;
      setAvailablePage(next);
      fetchAvailableResidents(detailGroup.id, next);
    }
  };

  const prevAvailablePage = () => {
    if (availablePagination.hasPreviousPage) {
      const prev = availablePage - 1;
      setAvailablePage(prev);
      fetchAvailableResidents(detailGroup.id, prev);
    }
  };

  const goToAddStep = (step) => {
    setAddStepTransition('exiting');
    setTimeout(() => {
      setAddStep(step);
      setAddStepTransition('entering');
    }, 150);
  };

  const handleSelectResident = (resident) => {
    setSelectedResidentId(resident.residentId || resident.id);
    setSelectedResident(resident);
    goToAddStep(1);
  };

  const handleAddResident = async () => {
    if (!selectedResidentId || !detailGroup) return;

    setAddResidentLoading(true);
    setAddResidentError(null);

    try {
      const payload = {
        residentId: selectedResidentId
      };
      await axiosInstance.post(`/api/Group/${detailGroup.id}/residents`, payload);

      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: 'Korisnik je uspešno dodat u grupu'
      });
      setSelectedResidentId('');
      setSelectedResident(null);
      setAddStep(0);
      setIsAddResidentOpen(false);
      fetchGroupResidents(detailGroup.id, residentsPage);
      setAvailablePage(1);
      fetchAvailableResidents(detailGroup.id, 1);

      setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 5000);
    } catch (error) {
      console.error('Error adding resident to group:', error);
      setAddResidentError(error.response?.data?.message || 'Greška pri dodavanju korisnika u grupu');
    } finally {
      setAddResidentLoading(false);
    }
  };

  const openRemoveResidentModal = (resident) => {
    setResidentToRemove(resident);
    setRemoveResidentError(null);
    setIsRemoveModalOpen(true);
  };

  const handleRemoveResident = async () => {
    if (!residentToRemove || !detailGroup) return;

    setRemoveResidentLoading(true);
    setRemoveResidentError(null);

    try {
      const residentId = residentToRemove.residentId || residentToRemove.id;
      await axiosInstance.delete(`/api/Group/${detailGroup.id}/residents/${residentId}`);

      setToast({
        show: true,
        kind: 'success',
        title: 'Uspešno!',
        subtitle: 'Korisnik je uspešno uklonjen iz grupe'
      });
      setIsRemoveModalOpen(false);
      setResidentToRemove(null);
      fetchGroupResidents(detailGroup.id, residentsPage);
      setAvailablePage(1);
      fetchAvailableResidents(detailGroup.id, 1);

      setTimeout(() => {
        setToast(prev => ({ ...prev, show: false }));
      }, 5000);
    } catch (error) {
      console.error('Error removing resident from group:', error);
      setRemoveResidentError(error.response?.data?.message || 'Greška pri uklanjanju korisnika iz grupe');
    } finally {
      setRemoveResidentLoading(false);
    }
  };

  const openDeleteGroupSidebar = () => {
    setIsDeleteGroupSidebarOpen(true);
  };

  const handleDeleteGroupSuccess = () => {
    setToast({
      show: true,
      kind: 'success',
      title: 'Uspešno!',
      subtitle: 'Grupa je uspešno obrisana'
    });
    closeGroupDetail();
    fetchGroups();

    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      setFormError('Molimo popunite naziv grupe');
      return;
    }

    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        name: formData.name,
        description: formData.description
      };

      if (editingGroup) {
        await axiosInstance.put(`/api/Group/${editingGroup.id}`, payload);
      } else {
        await axiosInstance.post('/api/Group', payload);
      }

      setIsSidePanelOpen(false);
      fetchGroups();
    } catch (error) {
      console.error('Error saving group:', error);
      setFormError(error.response?.data?.message || 'Greška pri čuvanju grupe');
    } finally {
      setFormLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  return (
    <PageContainer bare={embedded}>
      <div className="groups-container">
        {/* Toolbar */}
        <div className="groups-toolbar">
          <div className="groups-toolbar-search">
            <Search
              size="lg"
              placeholder="Pretražite grupe..."
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={() => {
                setSearchTerm('');
                setPage(1);
              }}
              labelText=""
              closeButtonLabelText="Obriši pretragu"
              style={{ flex: '1 1 250px', maxWidth: '400px' }}
            />
          </div>
          {canManageGroups && (
            <Button
              kind="primary"
              renderIcon={Add}
              onClick={() => openSidePanel()}
              className="add-group-button"
            >
              Dodaj grupu
            </Button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="groups-grid groups-grid--loading">
            {[...Array(4)].map((_, index) => (
              <SkeletonPlaceholder
                key={index}
                style={{
                  width: '100%',
                  height: '180px',
                  borderRadius: '8px'
                }}
              />
            ))}
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div style={{
            position: 'fixed',
            top: '4rem',
            right: '1rem',
            zIndex: 9999,
            minWidth: '320px',
            animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <style>
              {`
                @keyframes slideInFromRight {
                  from {
                    transform: translateX(calc(100% + 1rem));
                    opacity: 0;
                  }
                  to {
                    transform: translateX(0);
                    opacity: 1;
                  }
                }
              `}
            </style>
            <InlineNotification
              kind="error"
              title="Greška"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
              hideCloseButton={false}
              lowContrast
            />
          </div>
        )}

        {/* Grid */}
        {!loading && !error && groups.length > 0 && (
          <div className="groups-grid">
            {groups.map((group) => (
              <div
                key={group.id}
                className={`group-card ${group.isDefault ? 'group-card-default' : ''} group-card-clickable`}
                onClick={() => openGroupDetail(group)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openGroupDetail(group);
                  }
                }}
              >
                {/* Header */}
                <div className="group-card-header">
                  <div className="group-name">
                    <Group size={24} className="group-icon" />
                    <span className="group-name-text">{group.name}</span>
                  </div>
                  {group.isDefault && (
                    <Tag type="blue" size="sm">
                      Podrazumevana
                    </Tag>
                  )}
                </div>

                {/* Description */}
                <div className="group-card-content">
                  <div className="group-card-detail">
                    <span className="detail-label">Opis:</span>
                    <span className="detail-value">
                      {group.description || '-'}
                    </span>
                  </div>
                </div>

                {/* Footer */}
                <div className="group-card-footer">
                  <span className="group-card-details-hint">
                    <ArrowRight size={14} />
                    Detalji
                  </span>
                  <span className="group-card-date">
                    {formatDate(group.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && groups.length === 0 && (
          <div className="groups-empty">
            <div className="empty-icon">
              <Group size={64} />
            </div>
            <h3 className="empty-title">Nema grupa</h3>
            <p className="empty-subtitle">
              {canManageGroups
                ? 'Dodajte prvu grupu klikom na dugme iznad'
                : 'Trenutno nema grupa.'}
            </p>
          </div>
        )}

        {/* No Results */}
        {!loading && !error && groups.length === 0 && searchTerm && (
          <div className="groups-empty">
            <div className="empty-icon">🔍</div>
            <h3 className="empty-title">Nema rezultata</h3>
            <p className="empty-subtitle">Pokušajte sa drugom pretragom</p>
          </div>
        )}

        {/* Paginacija - ispod liste */}
        {!loading && !error && groups.length > 0 && (
          <div className="groups-pagination groups-pagination--bottom">
            <Button
              kind="ghost"
              size="md"
              renderIcon={ChevronLeft}
              onClick={prevPage}
              hasIconOnly
              disabled={!pagination.hasPreviousPage}
              iconDescription="Prethodna stranica"
            />
            <span className="groups-page">
              {page}/{pagination.totalPages}
            </span>
            <Button
              kind="ghost"
              size="md"
              renderIcon={ChevronRight}
              onClick={nextPage}
              hasIconOnly
              disabled={!pagination.hasNextPage}
              iconDescription="Sledeća stranica"
            />
          </div>
        )}

        {/* Right Side Panel - Add/Edit */}
        <div className={`group-side-panel ${isSidePanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h2>{editingGroup ? 'Izmeni grupu' : 'Dodaj grupu'}</h2>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              onClick={() => setIsSidePanelOpen(false)}
            />
          </div>
          {formError && (
            <div style={{
              position: 'fixed',
              top: '4rem',
              right: '1rem',
              zIndex: 9999,
              minWidth: '320px',
              animation: 'slideInFromRight 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <style>
                {`
                  @keyframes slideInFromRight {
                    from {
                      transform: translateX(calc(100% + 1rem));
                      opacity: 0;
                    }
                    to {
                      transform: translateX(0);
                      opacity: 1;
                    }
                  }
                `}
              </style>
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={formError}
                onCloseButtonClick={() => setFormError(null)}
                hideCloseButton={false}
                lowContrast
              />
            </div>
          )}
          <div className="side-panel-content">
            <form onSubmit={handleSubmit} className="group-form">
              <TextInput
                id="name"
                labelText="Naziv grupe *"
                placeholder="npr. Dijabetičari"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                disabled={formLoading}
              />

              <TextArea
                id="description"
                labelText="Opis"
                placeholder="Opis grupe..."
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                disabled={formLoading}
                rows={4}
              />

              <div className="form-actions">
                <Button
                  kind="secondary"
                  onClick={() => setIsSidePanelOpen(false)}
                  disabled={formLoading}
                >
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={formLoading}
                >
                  {formLoading ? 'Čuvanje...' : editingGroup ? 'Ažuriraj' : 'Sačuvaj'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Group Detail Side Panel */}
        <div className={`group-side-panel ${isDetailPanelOpen ? 'open' : ''}`}>
          <div className="side-panel-header">
            <div className="group-detail-header-title">
              {isAddResidentOpen && (
                <Button
                  kind="ghost"
                  size="sm"
                  renderIcon={ArrowLeft}
                  hasIconOnly
                  onClick={() => setIsAddResidentOpen(false)}
                  iconDescription="Nazad na detalje"
                  aria-label="Nazad na detalje"
                />
              )}
              <h2>Detalji grupe</h2>
            </div>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              onClick={closeGroupDetail}
            />
          </div>
          <div className="side-panel-content">
            {detailLoading && (
              <div className="group-detail-loading">
                <Loading description="Učitavanje grupe..." withOverlay={false} small />
              </div>
            )}

            {detailError && !detailLoading && (
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={detailError}
                onCloseButtonClick={() => setDetailError(null)}
                hideCloseButton={false}
                lowContrast
              />
            )}

            {detailGroup && !detailLoading && (
              <div className="group-detail">
                {!isAddResidentOpen && (
                  <>
                {/* Header */}
                <div className="group-detail-header">
                  <div className="group-detail-icon">
                    <Group size={32} />
                  </div>
                  <div className="group-detail-title">
                    <div className="group-detail-title-row">
                      <h3>{detailGroup.name}</h3>
                      {!detailGroup.isDefault && canManageGroups && (
                        <button
                          className="group-detail-delete-icon"
                          onClick={openDeleteGroupSidebar}
                          aria-label="Obriši grupu"
                          title="Obriši grupu"
                        >
                          <TrashCan size={16} />
                        </button>
                      )}
                    </div>
                    {detailGroup.isDefault && (
                      <span className="group-detail-default">
                        <CheckmarkFilled size={14} />
                        Podrazumevana grupa
                      </span>
                    )}
                  </div>
                </div>

                {/* Info rows */}
                <div className="group-detail-info">
                  <div className="group-detail-row">
                    <span className="detail-label">Opis:</span>
                    <span className="detail-value">{detailGroup.description || '-'}</span>
                  </div>
                </div>

                {/* Add Resident / Add Activity buttons */}
                {(canManageMembers || canManageActivities) && (
                <div className="group-detail-add-resident">
                  <div className="group-detail-action-buttons">
                    {canManageMembers && (
                      <Button
                        kind="tertiary"
                        size="md"
                        renderIcon={UserFollow}
                        onClick={() => {
                          setAddStep(0);
                          setSelectedResidentId('');
                          setSelectedResident(null);
                          setAddResidentError(null);
                          setAvailablePage(1);
                          fetchAvailableResidents(detailGroup.id, 1);
                          setIsAddResidentOpen(true);
                        }}
                        className="group-add-resident-toggle"
                      >
                        Dodaj korisnika
                      </Button>
                    )}
                    {canManageActivities && (
                      <Button
                        kind="tertiary"
                        size="md"
                        renderIcon={Activity}
                        onClick={() => setIsActivitiesSidebarOpen(true)}
                        className="group-add-activity-toggle"
                      >
                        Aktivnosti
                      </Button>
                    )}
                  </div>
                </div>
                )}
                  </>
                )}

                {/* Add Resident */}
                <div className="group-detail-add-resident">
                  {isAddResidentOpen && (
                    <div className="group-add-resident-form">
                      {/* Step title */}
                      <div className="group-add-step-title">
                        <h4>
                          {addStep === 0 && 'Izbor korisnika'}
                          {addStep === 1 && 'Potvrda dodavanja'}
                        </h4>
                      </div>

                      {/* Progress Indicator */}
                      <div className="custom-progress-bar">
                        <div className="progress-steps">
                          {[0, 1].map((step) => (
                            <React.Fragment key={step}>
                              <div
                                className={`progress-step ${
                                  addStep >= step ? 'completed' : ''
                                } ${addStep === step ? 'active' : ''}`}
                              >
                                <div className="step-circle">
                                  {addStep > step ? <span style={{ fontSize: '16px' }}>✓</span> : step + 1}
                                </div>
                              </div>
                              {step < 1 && (
                                <div className={`progress-line ${addStep > step ? 'completed' : ''}`}></div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>

                      {addResidentError && (
                        <InlineNotification
                          kind="error"
                          title="Greška"
                          subtitle={addResidentError}
                          onCloseButtonClick={() => setAddResidentError(null)}
                          hideCloseButton={false}
                          lowContrast
                        />
                      )}

                      {/* Step 0: Select resident */}
                      {addStep === 0 && (
                        <div className={`step-content ${addStepTransition}`}>
                          <div className="group-add-step-title">
                            <h4>Izaberite korisnika</h4>
                            <div className="group-add-pagination">
                              <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={ChevronLeft}
                                onClick={prevAvailablePage}
                                hasIconOnly
                                disabled={!availablePagination.hasPreviousPage}
                                iconDescription="Prethodna stranica"
                              />
                              <span className="group-add-page">
                                {availablePage}/{availablePagination.totalPages}
                              </span>
                              <Button
                                kind="ghost"
                                size="sm"
                                renderIcon={ChevronRight}
                                onClick={nextAvailablePage}
                                hasIconOnly
                                disabled={!availablePagination.hasNextPage}
                                iconDescription="Sledeća stranica"
                              />
                            </div>
                          </div>

                          {availableResidentsLoading && (
                            <div className="group-residents-loading">
                              <Loading description="Učitavanje korisnika..." withOverlay={false} small />
                            </div>
                          )}

                          {availableResidentsError && !availableResidentsLoading && (
                            <InlineNotification
                              kind="error"
                              title="Greška"
                              subtitle={availableResidentsError}
                              onCloseButtonClick={() => setAvailableResidentsError(null)}
                              hideCloseButton={false}
                              lowContrast
                            />
                          )}

                          {!availableResidentsLoading && !availableResidentsError && availableResidents.length > 0 && (
                            <div className="group-residents-list">
                              {availableResidents.map((resident) => {
                                const initials = getInitials(resident.firstName, resident.lastName);
                                const hasImage = resident.imgSrc && resident.imgSrc !== '';
                                const isSelected = (resident.residentId || resident.id) === selectedResidentId;
                                return (
                                  <div
                                    key={resident.residentId || resident.id}
                                    className={`group-resident-item group-resident-selectable ${isSelected ? 'group-resident-selected' : ''}`}
                                    onClick={() => handleSelectResident(resident)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSelectResident(resident);
                                      }
                                    }}
                                  >
                                    <div className="group-resident-avatar">
                                      {hasImage ? (
                                        <>
                                          <img
                                            src={resident.imgSrc}
                                            alt={resident.fullName}
                                            className="group-resident-image"
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                              e.target.nextElementSibling.style.display = 'flex';
                                            }}
                                          />
                                          <div
                                            className="group-resident-initials"
                                            style={{ display: 'none' }}
                                          >
                                            {initials || '?'}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="group-resident-initials">
                                          {initials || '?'}
                                        </div>
                                      )}
                                    </div>
                                    <div className="group-resident-info">
                                      <span className="group-resident-name">{resident.fullName}</span>
                                      <span className="group-resident-meta">
                                        {resident.age !== undefined && resident.age !== null
                                          ? `${resident.age} god.`
                                          : ''}
                                        {resident.roomName ? ` • Soba ${resident.roomName}` : ''}
                                      </span>
                                    </div>
                                    <ArrowRight size={16} className="group-resident-arrow" />
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {!availableResidentsLoading && !availableResidentsError && availableResidents.length === 0 && (
                            <div className="group-residents-empty">
                              <User size={24} />
                              <span>Nema korisnika koji nisu u grupi</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Step 1: Confirm */}
                      {addStep === 1 && (
                        <div className={`step-content ${addStepTransition}`}>
                          <div className="group-add-confirm">
                            <h4>Potvrda dodavanja</h4>
                            <p>Da li ste sigurni da želite da dodate sledećeg korisnika u grupu <strong>{detailGroup.name}</strong>?</p>

                            {selectedResident && (
                              <div className="group-add-confirm-resident">
                                <div className="group-resident-avatar">
                                  {selectedResident.imgSrc && selectedResident.imgSrc !== '' ? (
                                    <img
                                      src={selectedResident.imgSrc}
                                      alt={selectedResident.fullName}
                                      className="group-resident-image"
                                    />
                                  ) : (
                                    <div className="group-resident-initials">
                                      {getInitials(selectedResident.firstName, selectedResident.lastName) || '?'}
                                    </div>
                                  )}
                                </div>
                                <div className="group-resident-info">
                                  <span className="group-resident-name">{selectedResident.fullName}</span>
                                  <span className="group-resident-meta">
                                    {selectedResident.age !== undefined && selectedResident.age !== null
                                      ? `${selectedResident.age} god.`
                                      : ''}
                                    {selectedResident.roomName ? ` • Soba ${selectedResident.roomName}` : ''}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Navigation Buttons */}
                      <div className="form-actions">
                        {addStep > 0 && (
                          <Button
                            kind="secondary"
                            onClick={() => goToAddStep(0)}
                            disabled={addResidentLoading}
                          >
                            Nazad
                          </Button>
                        )}
                        {addStep < 1 ? (
                          <Button
                            kind="primary"
                            onClick={() => {
                              if (selectedResidentId) {
                                goToAddStep(1);
                              } else {
                                setAddResidentError('Molimo izaberite korisnika');
                                setTimeout(() => setAddResidentError(null), 3000);
                              }
                            }}
                            disabled={addResidentLoading}
                          >
                            Dalje
                          </Button>
                        ) : (
                          <Button
                            kind="primary"
                            renderIcon={CheckmarkFilled}
                            onClick={handleAddResident}
                            disabled={addResidentLoading}
                          >
                            {addResidentLoading ? 'Dodavanje...' : 'Potvrdi'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Residents */}
                {!isAddResidentOpen && (
                <div className="group-detail-residents">
                  <div className="group-detail-section-title group-residents-header">
                    <h4>Korisnici ({groupResidents.length})</h4>
                    <div className="group-residents-pagination">
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={ChevronLeft}
                        onClick={prevResidentsPage}
                        hasIconOnly
                        disabled={!residentsPagination.hasPreviousPage}
                        iconDescription="Prethodna stranica"
                      />
                      <span className="group-residents-page">
                        {residentsPage}/{residentsPagination.totalPages}
                      </span>
                      <Button
                        kind="ghost"
                        size="sm"
                        renderIcon={ChevronRight}
                        onClick={nextResidentsPage}
                        hasIconOnly
                        disabled={!residentsPagination.hasNextPage}
                        iconDescription="Sledeća stranica"
                      />
                    </div>
                  </div>

                  {groupResidentsLoading && (
                    <div className="group-residents-loading">
                      <Loading description="Učitavanje korisnika..." withOverlay={false} small />
                    </div>
                  )}

                  {groupResidentsError && !groupResidentsLoading && (
                    <InlineNotification
                      kind="error"
                      title="Greška"
                      subtitle={groupResidentsError}
                      onCloseButtonClick={() => setGroupResidentsError(null)}
                      hideCloseButton={false}
                      lowContrast
                    />
                  )}

                  {!groupResidentsLoading && !groupResidentsError && groupResidents.length > 0 && (
                    <div className="group-residents-list">
                      {groupResidents.map((resident) => {
                        const initials = getInitials(resident.firstName, resident.lastName);
                        const hasImage = resident.imgSrc && resident.imgSrc !== '';
                        return (
                          <div
                            key={resident.residentId || resident.id}
                            className="group-resident-item"
                            onClick={() => navigate(`/residents/${resident.residentId || resident.id}`, { state: { from: { label: 'Grupe', route: '/residents/groups' } } })}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                navigate(`/residents/${resident.residentId || resident.id}`, { state: { from: { label: 'Grupe', route: '/residents/groups' } } });
                              }
                            }}
                          >
                            <div className="group-resident-avatar">
                              {hasImage ? (
                                <>
                                  <img
                                    src={resident.imgSrc}
                                    alt={resident.fullName}
                                    className="group-resident-image"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextElementSibling.style.display = 'flex';
                                    }}
                                  />
                                  <div
                                    className="group-resident-initials"
                                    style={{ display: 'none' }}
                                  >
                                    {initials || '?'}
                                  </div>
                                </>
                              ) : (
                                <div className="group-resident-initials">
                                  {initials || '?'}
                                </div>
                              )}
                            </div>
                            <div className="group-resident-info">
                              <span className="group-resident-name">{resident.fullName}</span>
                              <span className="group-resident-meta">
                                {resident.age !== undefined && resident.age !== null
                                  ? `${resident.age} god.`
                                  : ''}
                                {resident.roomName ? ` • Soba ${resident.roomName}` : ''}
                              </span>
                            </div>
                            <div className="group-resident-actions">
                              {!detailGroup.isDefault && canManageMembers && (
                                <Button
                                  kind="ghost"
                                  size="sm"
                                  renderIcon={TrashCan}
                                  hasIconOnly
                                  iconDescription="Ukloni iz grupe"
                                  className="group-resident-remove-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openRemoveResidentModal(resident);
                                  }}
                                />
                              )}
                              <ArrowRight size={16} className="group-resident-arrow" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!groupResidentsLoading && !groupResidentsError && groupResidents.length === 0 && (
                    <div className="group-residents-empty">
                      <User size={24} />
                      <span>Nema korisnika u ovoj grupi</span>
                    </div>
                  )}
                </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Detail Panel Overlay */}
        {isDetailPanelOpen && (
          <div
            className="side-panel-overlay"
            onClick={closeGroupDetail}
          />
        )}

        {/* Group Activities Sidebar */}
        <GroupActivitiesSidebar
          isOpen={isActivitiesSidebarOpen}
          onClose={() => setIsActivitiesSidebarOpen(false)}
          group={detailGroup}
          canManageActivities={canManageActivities}
          onAddActivity={() => {
            setIsActivitiesSidebarOpen(false);
            setIsAddActivityOpen(true);
          }}
        />

        {/* Group Add Activity Sidebar */}
        <GroupAddActivitySidebar
          isOpen={isAddActivityOpen}
          onClose={() => setIsAddActivityOpen(false)}
          group={detailGroup}
          onSuccess={() => {
            setToast({
              show: true,
              kind: 'success',
              title: 'Uspešno!',
              subtitle: 'Aktivnost je uspešno dodata u grupu'
            });
            setTimeout(() => setToast(prev => ({ ...prev, show: false })), 5000);
          }}
        />

        {/* Toast Notification */}
        {toast.show && (
          <div className="toast-container">
            <ToastNotification
              kind={toast.kind}
              title={toast.title}
              subtitle={toast.subtitle}
              timeout={0}
              onClose={() => setToast(prev => ({ ...prev, show: false }))}
              lowContrast
            />
          </div>
        )}

        {/* Remove Resident Confirmation Modal */}
        <Modal
          open={isRemoveModalOpen}
          onRequestClose={() => setIsRemoveModalOpen(false)}
          modalHeading="Ukloni korisnika iz grupe"
          primaryButtonText={removeResidentLoading ? 'Uklanjanje...' : 'Ukloni'}
          secondaryButtonText="Otkaži"
          primaryButtonDisabled={removeResidentLoading}
          onRequestSubmit={handleRemoveResident}
          danger
        >
          <p>
            Da li ste sigurni da želite da uklonite korisnika{' '}
            <strong>{residentToRemove?.fullName}</strong> iz grupe{' '}
            <strong>{detailGroup?.name}</strong>?
          </p>
          {removeResidentError && (
            <div style={{ marginTop: '0.75rem', color: '#da1e28', fontSize: '0.8125rem' }}>
              {removeResidentError}
            </div>
          )}
        </Modal>

        {/* Delete Group Sidebar */}
        <DeleteGroupSidebar
          isOpen={isDeleteGroupSidebarOpen}
          onClose={() => setIsDeleteGroupSidebarOpen(false)}
          group={detailGroup}
          residentCount={groupResidents.length}
          onSuccess={handleDeleteGroupSuccess}
        />

        {/* Floating Action Button - Dodaj grupu (mobile only) */}
        {canManageGroups && !isSidePanelOpen && ReactDOM.createPortal(
          <button
            className="floating-button group-fab"
            onClick={() => openSidePanel()}
            style={{
              position: 'fixed',
              bottom: '2rem',
              right: '2rem',
              height: '56px',
              padding: '0 24px',
              backgroundColor: '#9fb746',
              border: 'none',
              borderRadius: '28px',
              display: 'none',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              gap: '12px',
              outline: 'none',
              zIndex: 9999
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#8da63d';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#9fb746';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(0.97)';
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px) scale(1)';
            }}
          >
            <Add
              size={24}
              style={{
                flexShrink: 0,
                minWidth: '24px',
                minHeight: '24px',
                fill: '#ffffff'
              }}
              aria-label="Dodaj grupu"
              color="#ffffff"
            />
            <span style={{
              color: 'white',
              fontWeight: '500',
              fontSize: '0.95rem',
              whiteSpace: 'nowrap',
              userSelect: 'none'
            }}>
              Dodaj grupu
            </span>
          </button>,
          document.body
        )}

      </div>
    </PageContainer>
  );
};

export default Groups;
